import { Link } from "react-router-dom";

import {
  formatDate,
  formatObjective,
  formatPercent,
  formatSport,
  formatWeekType,
} from "@/lib/coachingFormatters";
import { formatTrainingDaysSummary } from "@/lib/workoutPrescriptionFormatters";
import {
  enrichCoachSession,
  activePlanBlockDates,
  activePlanUpcomingSessions,
  activePlanWeekLabel,
  trainingPlanPeriodStatus,
} from "@/lib/activePlanDisplay";
import {
  isActiveTrainingBlock,
  primaryGoalLabel,
  primarySportLabel,
} from "@/lib/trainingPlanWorkflow";
import { CoachingSectionCard } from "@/components/coaching/CoachingSectionCard";
import { ScheduledWorkoutCard } from "@/components/coaching/ScheduledWorkoutCard";
import { Button } from "@/components/ui/button";

const Metric = ({ label, value, testId }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-dark-text-muted">
      {label}
    </div>
    <div className="text-sm text-dark-text-primary" data-testid={testId}>
      {value ?? "—"}
    </div>
  </div>
);

export const ActiveTrainingPlanCard = ({
  block,
  goal,
  athlete,
  week,
  today,
  upcoming = [],
  compliance,
  timezone,
  activePlan,
  onChangePlan,
  onEditSession,
}) => {
  if (!isActiveTrainingBlock(block)) {
    return null;
  }

  const todaySummary = (today?.scheduled_sessions || [])[0];
  const todaySession = enrichCoachSession(todaySummary, activePlan);
  const upcomingSessions = activePlanUpcomingSessions(activePlan, {
    todayDate: today?.date,
    todaySessionId: todaySummary?.session_id,
    fallbackUpcoming: upcoming,
  });
  const sport = primarySportLabel({
    athlete,
    todaySessions: today?.scheduled_sessions || [],
  })
    || activePlan?.primary_sport
    || block?.primary_sport;
  const goalLabel = primaryGoalLabel(goal);
  const trainingDays = formatTrainingDaysSummary(athlete?.weekly_availability_summary);
  const progress = week?.completed_planned_minutes != null && week?.target_total_minutes
    ? formatPercent(week.completed_planned_minutes / week.target_total_minutes)
    : compliance?.weekly_compliance_score != null
      ? formatPercent(compliance.weekly_compliance_score)
      : null;
  const {
    start: blockStartDate,
    end: blockEndDate,
    finalWorkoutDate,
  } = activePlanBlockDates(
    activePlan,
    block,
    timezone,
  );
  const weekLabel = activePlanWeekLabel(activePlan, block, week);
  const periodStatus = trainingPlanPeriodStatus({
    today: today?.date || today,
    start: blockStartDate,
    end: blockEndDate,
  });
  const periodTone = periodStatus === "Active"
    ? "border-analytics-green/40 bg-analytics-green/10 text-analytics-green"
    : periodStatus === "Upcoming"
      ? "border-analytics-amber/40 bg-analytics-amber/10 text-analytics-amber"
      : "border-[var(--border-primary)] bg-[var(--surface-secondary)] text-dark-text-secondary";

  return (
    <CoachingSectionCard
      title="Active training plan"
      subtitle="Authoritative plan from your recorded training schedule"
      headingId="active-training-plan-heading"
      testId="active-training-plan-card"
      actions={(
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onChangePlan}
          data-testid="active-plan-change"
        >
          Change plan
        </Button>
      )}
    >
      <div
        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] uppercase tracking-[0.2em] ${periodTone}`}
        data-testid="active-training-plan-status"
      >
        {periodStatus}
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Metric
          label="Sport"
          value={sport ? formatSport(sport) : "—"}
          testId="active-plan-sport"
        />
        <Metric
          label="Primary goal"
          value={goalLabel ? formatObjective(goalLabel) : "—"}
          testId="active-plan-goal"
        />
        <Metric
          label="Plan start"
          value={formatDate(blockStartDate, timezone)}
          testId="active-plan-start"
        />
        <Metric
          label="Plan end"
          value={formatDate(blockEndDate, timezone)}
          testId="active-plan-end"
        />
        <Metric
          label="Last workout"
          value={formatDate(finalWorkoutDate, timezone)}
          testId="active-plan-last-workout"
        />
        <Metric
          label="Current week"
          value={weekLabel || "—"}
          testId="active-plan-week"
        />
        <Metric
          label="Week focus"
          value={formatWeekType(week?.week_type || block?.recommended_phase)}
          testId="active-plan-week-type"
        />
        <Metric
          label="Progress"
          value={progress}
          testId="active-plan-progress"
        />
        {trainingDays && (
          <Metric
            label="Training days"
            value={trainingDays}
            testId="active-plan-training-days"
          />
        )}
      </div>

      {todaySession && (
        <div className="mt-5" data-testid="active-plan-today-workout">
          <div className="text-[11px] uppercase tracking-[0.25em] text-dark-text-muted mb-2">
            Today&apos;s workout
          </div>
          <ScheduledWorkoutCard
            session={todaySession}
            timezone={timezone}
            testIdPrefix="active-plan-workout"
            defaultExpanded
            onEdit={onEditSession}
          />
        </div>
      )}

      {upcomingSessions.length > 0 && (
        <div className="mt-5 space-y-3" data-testid="active-plan-upcoming">
          <div className="text-[11px] uppercase tracking-[0.25em] text-dark-text-muted">
            Upcoming workouts
          </div>
          {upcomingSessions.map((session) => {
            const enriched = enrichCoachSession({
              ...session,
              planned_start_time: session.planned_start_time,
              intensity_target_summary: session.intensity_target_summary
                ?? session.intensity_summary
                ?? session.objective,
            }, activePlan);
            return (
            <ScheduledWorkoutCard
              key={session.session_id}
              session={enriched}
              timezone={timezone}
              testIdPrefix="active-plan-upcoming"
              onEdit={onEditSession}
            />
            );
          })}
        </div>
      )}

      <p className="mt-4 text-xs text-dark-text-muted">
        Update profile details such as experience in{" "}
        <Link to="/settings" className="text-brand-primary underline-offset-2 hover:underline">
          Settings
        </Link>
        {" "}before generating a replacement plan.
      </p>
    </CoachingSectionCard>
  );
};
